/**
 * A device command that Propipe AI suggested.
 *
 * A proposal is a suggestion and nothing more. The AI reaches the database
 * through a read-only account and cannot queue commands. Nothing is sent to a
 * device until an administrator confirms it.
 *
 * The fields below identify the proposal; they do not describe what would be
 * written. Settings, current values and warnings are read separately from
 * /chat/messages/{id}/actions/{actionId}, because an aggregate command writes
 * settings nobody asked about and those move after the answer was written.
 */
export interface ProposedDeviceCommandTarget {
  uid: number;
  transport: string;
  isConnected: boolean;
}

export interface ProposedDeviceCommand {
  actionId: string;
  commandType: string;
  summary: string;
  targets: ProposedDeviceCommandTarget[];
  expiresAtUtc: string;
  requiresTypedConfirmation: boolean;
}

export const CELLULAR_TRANSPORT = 'cellular';
export const IRIDIUM_TRANSPORT = 'iridium';

/**
 * Parses the proposedActionJson string from the server into typed proposals,
 * discarding anything that does not carry the fields the card needs to state
 * plainly what would be sent and to which devices.
 */
export function normalizeProposedDeviceCommands(
  proposedActionJson: unknown,
  existingProposals: unknown
): ProposedDeviceCommand[] {
  let proposals = existingProposals;
  if (typeof proposedActionJson === 'string' && proposedActionJson.length > 0) {
    try {
      proposals = JSON.parse(proposedActionJson);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(proposals)) {
    return [];
  }

  return proposals
    .filter(
      (proposal): proposal is ProposedDeviceCommand =>
        proposal != null &&
        typeof proposal === 'object' &&
        typeof proposal.actionId === 'string' &&
        proposal.actionId.length > 0 &&
        typeof proposal.commandType === 'string' &&
        typeof proposal.summary === 'string' &&
        typeof proposal.expiresAtUtc === 'string' &&
        Array.isArray(proposal.targets) &&
        proposal.targets.length > 0 &&
        proposal.targets.every(
          (target: unknown) =>
            target != null &&
            typeof target === 'object' &&
            typeof (target as ProposedDeviceCommandTarget).uid === 'number'
        )
    )
    .map((proposal) => ({
      ...proposal,
      requiresTypedConfirmation: proposal.requiresTypedConfirmation === true,
    }));
}

/**
 * Returns whether the proposal is too old to confirm. The server enforces the
 * same deadline; this only avoids offering a button that would be refused.
 */
export function isProposedCommandExpired(
  proposal: ProposedDeviceCommand,
  now: Date = new Date()
): boolean {
  const expiry = Date.parse(proposal.expiresAtUtc);
  if (Number.isNaN(expiry)) {
    return true;
  }
  return expiry <= now.getTime();
}

/**
 * Describes how the command would reach the devices, so the operator knows
 * whether they are about to spend Iridium credit.
 */
export function describeTransport(proposal: ProposedDeviceCommand): string {
  const transports = new Set(
    proposal.targets.map((target) => target.transport)
  );
  if (transports.size === 1) {
    return transports.has(IRIDIUM_TRANSPORT) ? 'Iridium' : 'Cellular';
  }
  return 'Cellular and Iridium';
}
